/**
 * Static config for the Risk Center page.
 *
 * Anything that doesn't change per-user (partner branding, scenario column
 * labels, mocked addresses, copy) lives here so the page components can be
 * dumb and the API stays narrow. Strategy + scenario *numbers* still come
 * from the backend so tests have a single source of truth.
 */

export const STRATEGY_FUTURES = 'futures';
export const STRATEGY_PUT = 'put';
export const STRATEGY_MINIMUM = 'minimum';

export const PARTNER = {
  name: 'Acme Risk Partners',
  shortName: 'Acme Risk',
  legalName: 'Acme Risk Partners LLC',
  registration: 'a registered futures commission merchant (mock)',
  office: {
    city: 'Saskatoon, SK',
    phone: '(306) 934-1177',
    address: '410 22nd Street East, Saskatoon, SK S7K 5T6',
  },
  scheduleUrl: 'https://acme-risk.example.com/schedule',
  advisorEmail: 'advisor@acme-risk.example.com',
  accountUrl: 'https://acme-risk.example.com/open-account',
};

export const SCENARIO_COLUMNS = [
  { key: 'no_hedge_revenue', label: 'No Hedge', strategyId: null },
  { key: 'futures_revenue', label: 'Futures Hedge', strategyId: STRATEGY_FUTURES },
  { key: 'put_revenue', label: 'Put Option', strategyId: STRATEGY_PUT },
  { key: 'minimum_revenue', label: 'Min Price', strategyId: STRATEGY_MINIMUM },
];

export const RISK_SCORE_META = {
  high: { label: 'High', color: 'red' },
  medium: { label: 'Medium', color: 'amber' },
  low: { label: 'Low', color: 'green' },
};

export const ACTION_CARDS = [
  {
    key: 'sinoa',
    icon: 'mdi-message-outline',
    title: 'Ask Sinoa another question',
    description: 'Get more insights and ideas',
  },
  {
    key: 'savePlan',
    icon: 'mdi-bookmark-outline',
    title: 'Save to Marketing Plan',
    description: 'Add this strategy to your plan',
  },
  {
    key: 'priceAlert',
    icon: 'mdi-bell-outline',
    title: 'Set Price Alert',
    description: 'Get notified at your target price',
  },
];

// Customize Assumptions modal — editable fields with mocked starting values.
export const ASSUMPTION_FIELDS = [
  { key: 'unsoldInventoryBu', label: 'Unsold inventory (bu)', default: 40000 },
  { key: 'currentPricePerMt', label: 'Current price ($/mt)', default: 450.00 },
  { key: 'futuresHedgePerMt', label: 'Futures level ($/mt)', default: 446.00 },
  { key: 'putStrikePerMt', label: 'Put strike ($/mt)', default: 440.00 },
  { key: 'putPremiumPerMt', label: 'Put premium ($/mt)', default: 8.00 },
  { key: 'minPriceFloorPerMt', label: 'Minimum price floor ($/mt)', default: 440.00 },
];

export const COMPLIANCE_FOOTER = 'GrainFox provides educational tools and market information. Trades and account services are provided by Acme Risk Partners LLC (mock advisor for demonstration).';

export const SCENARIO_DISCLAIMER = 'These scenarios are illustrative only and not guaranteed. Products are subject to suitability, approval, and market conditions.';

export const FULL_DISCLOSURES = 'Futures, options, and OTC products involve substantial risk of loss and may not be suitable for all investors. Strategies shown are illustrative and do not constitute a recommendation or guarantee. Execution, suitability, and account approvals are controlled by Acme Risk Partners. GrainFox provides educational tools and market information only.';

/**
 * Build the rich Sinoa explanation answer for the seeded chatbot exchange.
 * Pulls live numbers from the overview payload so the response reads as
 * tailored to the producer's actual exposure rather than boilerplate.
 *
 * @param {object} overview - the riskCenter overview state
 * @returns {string} a markdown-formatted answer
 */
export function buildExplanationAnswer(overview) {
  const unsold = Number(overview?.unsold_bushels || 0).toLocaleString();
  const commodity = overview?.commodity?.name || 'Soybeans';
  const commodityLower = commodity.toLowerCase();
  const cropYear = overview?.crop_year || 'the current crop year';
  const price = Number(overview?.avg_price_per_mt || 0).toFixed(2);
  const riskLabel = (overview?.risk_score || 'elevated').toString();

  return `**Why these educational approaches?**

Your farm currently has roughly **${unsold} bu of unsold ${commodityLower}** for ${cropYear}, exposed to the cash market at about **$${price}/mt**. With a **${riskLabel}** price-risk score, even a 10% downside move would translate into a meaningful revenue swing across that inventory. The three approaches below are educational frameworks for thinking about that exposure — they are not a personalized trade recommendation.

**1. Futures Hedge**

Selling futures equivalent to your unsold ${commodityLower} at roughly $446/mt sets a known price for sale time. If the market drops, the gain on the futures position offsets the lower cash price; if the market rallies, you forgo that upside. Basis risk (the difference between the futures and your local cash market) still applies. Best when the priority is **removing price uncertainty as much as possible**.

**2. Put Option**

Buying a put around $440/mt strike for a ~$8/mt premium establishes a **downside floor** while preserving the cash-market upside if prices rally. The trade-off is the upfront premium, which reduces net revenue in flat or slightly higher markets. Best when you want to **protect the downside but stay open to a rally**.

**3. Minimum Price Strategy**

Combines a floor (~$440/mt) with partial participation in any upside above the floor, less a structure cost. Mechanics, fees, and availability vary by product, jurisdiction, and account approval. Typically sits **between a futures hedge and a put** in cost and complexity. Best when you want **some protection without giving up all upside**.

**How to read the scenario table**

Each row models a market move from the current price (-10%, unchanged, +10%). The "No Hedge" column is what the inventory is worth at that price with no protection. The other columns show illustrative outcomes for each strategy at the same price — so you can see how each one shifts your revenue profile in different market conditions.

**What to do next**

These numbers are illustrative only. Actual pricing, structure, fees, and suitability depend on your account, jurisdiction, and live market conditions, and are determined by **Acme Risk Partners**. When you're ready to talk specifics, connect with a licensed Acme Risk advisor — they handle suitability review, execution, and account approval. GrainFox stays on the education and analytics side.`;
}

export const ACCOUNT_LINK_COPY = 'Account onboarding, KYC, suitability, and execution are controlled by Acme Risk Partners. You will be redirected to the Acme Risk-owned account-linking flow. GrainFox stores account connection status only after Acme Risk returns confirmation.';

export const SCHEDULE_SLOTS = [
  'Today, 2:30 PM',
  'Tomorrow, 9:00 AM',
  'Tomorrow, 1:00 PM',
];

export const SAVE_TO_PLAN_COPY = 'The selected strategy has been saved to your marketing plan as a Planning Scenario. It is editable, removable, and clearly labelled as illustrative — not a trade recommendation.';

export const PRICE_ALERT_COPY = 'Your price alert is active. We will notify you in-app and by email when soybeans cross your target. The alert will not place a trade — execution and suitability remain with Acme Risk Partners.';
